<template>
  <component
    v-on="$listeners"
    v-bind="$attrs"
    class="c-button"
    :class="buttonClass"
    :is="component"
    :to="to ? to : null"
    :href="external && to"
    :disabled="disabled || loading"
  >
    <slot
      name="text-before"
    />

    <c-icon
      class="icon"
      v-if="icon"
      :icon="icon"
      :size="fixedIconSize"
    />

    <span class="text">
      <slot />
    </span>
    <c-spinner
      v-if="loading"
      class="loading"
      size="sm"
    />
  </component>
</template>

<script>
export default {
  props: {
    to: [Object, String],
    size: {
      type: [Number, String],
      default: 'md',
      validator: (size) => ['sm', 'md', 'lg', 'xl'].includes(size)
    },
    after: Boolean,
    icon: String,
    iconSize: String,
    external: Boolean,
    primary: Boolean,
    alternative: Boolean,
    lightGrey: Boolean,
    warning: Boolean,
    block: Boolean,
    ghost: Boolean,
    borderOnly: Boolean,
    disabled: Boolean,
    large: Boolean,
    loading: Boolean
  },
  computed: {
    component () {
      if (this.to && this.external) {
        return 'a'
      } else if (this.to) {
        return 'router-link'
      }
      return 'button'
    },
    hasText () {
      const hasText = !!this.$slots.default
      return hasText
    },
    buttonClass () {
      const buttonClass = [
        '-' + this.size, {
          '-large': this.large,
          '-primary': this.primary,
          '-alternative': this.alternative,
          '-light-grey': this.lightGrey,
          '-warning': this.warning,
          '-icon': !!this.icon,
          '-icon-only': !this.hasText && this.icon,
          '-block': this.block,
          '-ghost': this.ghost,
          '-after': this.after,
          '-disabled': this.disabled || this.loading
        }]
      return buttonClass
    },
    fixedIconSize () {
      const sizes = {
        sm: '11',
        md: '15',
        lg: '19',
        xl: '17'
      }
      const fixedIconSize = this.iconSize || sizes[this.size]
      return fixedIconSize
    }
  }
}
</script>

<style lang="scss">
@import '~@style/reference';

$c-sm-button-height: 20px;
$c-md-button-height: 30px;
$c-lg-button-height: 40px;
$c-xl-button-height: 48px;

.c-button {
  height: $c-md-button-height;
  border-radius: calc(#{$c-md-button-height} / 2);
  min-width: $c-md-button-height;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid $border-color;
  line-height: $c-md-button-height;
  vertical-align: middle;
  text-decoration: none;
  background-color: transparent;
  transition: border-color .3s ease,
              box-shadow .3s ease;
  will-change: border, box-shadow;
  cursor: pointer;
  position: relative;

  .loading {
    margin-top: 10px;
    position: absolute;
    right: 10px;
    .c-spinner-circle {
      stroke: $secondary-color-placeholder;
      stroke: var(--color-secondary);
    }
  }

  &.-after {
    flex-flow: row-reverse;

    & > .icon {
      margin-left: 10px;
    }

    & > .text {
      margin-left: 0;
    }
  }

  &:focus {
    box-shadow: none;
    outline: none;
  }

  @include desktop {
    &:hover {
      border-color: $secondary-color-placeholder;
      border-color: var(--color-secondary);
      box-shadow: 0 0 0 2px $secondary-color-placeholder;
      box-shadow: 0 0 0 2px var(--color-secondary);
    }
  }

  & > .text {
    line-height: 1;
    text-transform: uppercase;
    color: $title-color;
    font-weight: 500;
    font-size: 12px;
  }

  &.-sm {
    height: $c-sm-button-height;
    line-height: $c-sm-button-height;
    border-radius: calc(#{$c-sm-button-height} / 2);
    min-width: $c-sm-button-height;
  }

  &.-lg {
    height: $c-lg-button-height;
    line-height: $c-lg-button-height;
    border-radius: calc(#{$c-lg-button-height} / 2);
    min-width: $c-lg-button-height;
  }

  &.-xl {
    height: $c-xl-button-height;
    line-height: $c-xl-button-height;
    border-radius: calc(#{$c-xl-button-height} / 2);
    min-width: $c-xl-button-height;
  }

  &.-primary,
  &.-alternative,
  &.-light-grey,
  &.-warning {
    border: none;
    transition: background-color .3s ease;
    will-change: background-color;

    &:hover { box-shadow: none; }

    & > .text { color: $white-color; }
  }
  &.-large {
    height: 58.24px;
    line-height: 40px;
    border-radius: calc(20px / 2);
    width: 324px;
  }
  &.-primary {
    background-color: $primary-color-placeholder;
    background-color: var(--color-primary);

    &:hover {
      background-color: rgba($primary-color-placeholder, .8);
      background-color: var(--color-primary-8);
    }
    &.-icon > .icon {
      fill: $secondary-color-placeholder;
      fill: var(--color-secondary);
    }
  }

  &.-alternative {
    background-color: $secondary-color-placeholder;
    background-color: var(--color-secondary);

    &.-icon > .icon {
      fill: $primary-color-placeholder;
      fill: var(--color-primary);
    }
    & > .text {
      color: $primary-color-placeholder;
      color: var(--color-primary);
    }
    @include desktop {
      &:hover {
        background-color: rgba($secondary-color-placeholder, .8);
        background-color: var(--color-secondary-8);
      }
    }
  }

  &.-light-grey {
    background-color: rgba($background-color, .1);
    &.-icon > .icon {
      fill: $background-color;
    }
  }

  &.-warning {
    background-color: $tertiary-color-placeholder;
    background-color: var(--color-tertiary);

    &:hover {
      background-color: rgba($primary-color-placeholder, .8);
      background-color: var(--color-tertiary-8);
    }
    &.-icon > .icon { fill: $icon-color; }
  }

  &:not(.-icon-only) {
    padding-left: 20px;
    padding-right: 20px;

    & > .icon { margin-right: 5px; }
  }

  &.-block {
    height: 55px;
    background-color: $primary-color-placeholder;
    background-color: var(--color-primary);
    border-radius: 0;
    width: 100%;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 5;

    & > .icon {
      fill: $secondary-color-placeholder;
      fill: var(--color-secondary);
    }

    & > .text {
      color: white;
      font-family: $title-font-family;
      font-weight: $title-font-weight;
      font-size: 12px;
      line-height: 16px;
    }
  }

  &.-ghost {
    background-color: transparent;
    border: none;

    & > .text { color: $text-color; }
    & > .icon { fill: $icon-color; }
  }

  &.-border-only { background-color: transparent; }

  &.-disabled {
    pointer-events: none;
    cursor: default;
    background-color: $disabled-button;

    &.-icon > .icon { fill: $disabled-font; }
    & > .text {
      color: $disabled-font;
      font-weight: bold;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/CComponents/CButton.vue